import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './CustomRadiusInput.scss';
import BottomRight from './Bottom_Right.png';
import TopLeft from './Top_Left.png';
import TopRight from './Top_Right.png';
import BottomLeft from './Bottom_Left.png';

// Parse comma-separated shorthand: "12, 34" => [12, 34]
const parseInputToArray = value => value
  .split(',')
  .map(v => v.trim())
  .filter(Boolean)
  .map(v => parseInt(v, 10))
  .filter(num => !Number.isNaN(num));

// Expand an array of length 1-4 to exactly 4 corners
const expandRadius = (values) => {
  switch (values.length) {
    case 1:
      return [values[0], values[0], values[0], values[0]];
    case 2:
      return [values[0], values[1], values[0], values[1]];
    case 3:
      return [values[0], values[1], values[2], values[1]];
    case 4:
      return values;
    default:
      return [0, 0, 0, 0];
  }
};

// Convert 4 corners to "12px 34px 56px 78px"
const toPxString = arr => arr.map(val => `${val}px`).join(' ');

const cornersToShorthand = ([a, b, c, d]) => {
  if (a === b && b === c && c === d) {
    // All four same
    return `${a}`;
  }
  if (a === c && b === d) {
    // Opposite corners
    return `${a}, ${b}`;
  }
  if (b === d) {
    // Three-value
    return `${a}, ${b}, ${c}`;
  }
  // Four distinct corners
  return `${a}, ${b}, ${c}, ${d}`;
};

function CustomRadiusInput({ value, onChange }) {
  // Toggle between single input or four corners
  const [locked, setLocked] = useState(false);

  // corners = [topLeft, topRight, bottomLeft, bottomRight]
  const [corners, setCorners] = useState([0, 0, 0, 0]);

  // singleValue is what we display in the 1-input mode
  // But we only overwrite it when toggling from locked -> unlocked
  const [singleValue, setSingleValue] = useState('');

  // Track the previous locked state to detect toggle transitions
  const prevLockedRef = useRef(locked);

  // Parse & expand the parent's `value` prop into corners on mount or when `value` changes
  useEffect(() => {
    const stripped = value.replace(/px/g, '').replace(/\s+/g, ',');
    const arr = parseInputToArray(stripped);
    const expanded = expandRadius(arr);

    setCorners(expanded);
    setSingleValue(arr.join(', '));
  }, []);

  useEffect(() => {
    if (prevLockedRef.current && !locked) {
      // We were locked, now unlocked
      setSingleValue(cornersToShorthand(corners));
    }
    prevLockedRef.current = locked;
  }, [locked]);

  // Single input changes
  const handleSingleInputChange = (e) => {
    let raw = e.target.value;
    // Only digits and commas, and remove multiple consecutive commas
    raw = raw.replace(/[^0-9,]/g, '').replace(/,+/g, ',');
    // Remove starting comma
    raw = raw.replace(/^,/, '');
    // Max 3 digits per segment
    const segments = raw.split(',').map(seg => seg.slice(0, 3));
    const cleaned = segments.join(',');

    setSingleValue(cleaned);

    // Convert to corners
    const arr = parseInputToArray(cleaned);
    setCorners(expandRadius(arr));
    onChange(toPxString(arr));
  };


  // Four corner changes
  const handleCornerChange = (index, e) => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length > 3) {
      val = val.slice(0, 3);
    }
    const numVal = parseInt(val, 10) || 0;

    const newCorners = [...corners];
    newCorners[index] = numVal;
    setCorners(newCorners);
    // We won't immediately overwrite singleValue, because
    // we only do that when toggling from locked->unlocked
    onChange(toPxString(newCorners));
  };

  const toggleLocked = () => {
    setLocked(prev => !prev);
  };

  return (
    <div className={styles.customRadius}>
      <div className={styles.unlock}>
        <div className={styles.customRadiusHeader}>Custom Radius</div>

        {/* UNLOCKED MODE: Single Input */}
        {!locked && (
          <div className={styles.singleInputRow}>
            <div className={styles.inputContainer}>
              <input
                className={styles.singleInput}
                id="singleRadiusInput"
                type="text"
                value={singleValue}
                onChange={handleSingleInputChange}
                placeholder="e.g. 12, 34, 56, 78"
              />
              <span className={styles.suffix}>px</span>
            </div>
          </div>
        )}

            {/* Lock Toggle Button */}
      <div className={styles.lockToggle}>
               <button
                 type="button"
                 className={styles.expandButton}
                 onClick={toggleLocked}
                 style={{
                   backgroundColor: locked ? '#88BB4033' : '#DEE2E6',
                 }}
               >
                 <span role="img" aria-label="Expand">⛶</span>
               </button>
      </div>


      </div>
              {/* LOCKED MODE: Four Inputs (top-left, top-right, bottom-left, bottom-right) */}
              {locked && (
          <div className={styles.customRadiusInputs}>
            {/* Top-Left */}
            <div className={styles.corner}>
              <div className={styles.inputContainer}>
                <input
                  className={styles.input}
                  id="topLeftInput"
                  type="text"
                  value={corners[0] || ''}
                  onChange={e => handleCornerChange(0, e)}
                  placeholder="0"
                />
                <span className={styles.suffix}>px</span>
              </div>
              <img src={TopLeft} alt="top left" />
            </div>

            {/* Top-Right */}
            <div className={styles.corner}>
              <img src={TopRight} alt="top right" />
              <div className={styles.inputContainer}>
                <input
                  className={styles.input}
                  id="topRightInput"
                  type="text"
                  value={corners[1] || ''}
                  onChange={e => handleCornerChange(1, e)}
                  placeholder="0"
                />
                <span className={styles.suffix}>px</span>
              </div>
            </div>

            {/* Bottom-Left */}
            <div className={styles.corner}>
              <div className={styles.inputContainer}>
                <input
                  className={styles.input}
                  id="bottomLeftInput"
                  type="text"
                  value={corners[3] || ''}
                  onChange={e => handleCornerChange(3, e)}
                  placeholder="0"
                />
                <span className={styles.suffix}>px</span>
              </div>
              <img src={BottomLeft} alt="bottom left" />
            </div>

            {/* Bottom-Right */}
            <div className={styles.corner}>
              <img src={BottomRight} alt="bottom right" />
              <div className={styles.inputContainer}>
                <input
                  className={styles.input}
                  id="bottomRightInput"
                  type="text"
                  value={corners[2] || ''}
                  onChange={e => handleCornerChange(2, e)}
                  placeholder="0"
                />
                <span className={styles.suffix}>px</span>
              </div>
            </div>
          </div>
              )}
    </div>
  );
}

CustomRadiusInput.propTypes = {
  /** A 4-value px string like "12px 34px 12px 34px" or a shorthand like "12px" */
  value: PropTypes.string,
  /** Callback receiving the expanded 4-value px string (e.g. "12px 34px 56px 78px") */
  onChange: PropTypes.func.isRequired,
};

CustomRadiusInput.defaultProps = {
  value: '',
};

export default CustomRadiusInput;
